/**
 * <SiteDecommissionDialog> — admin-tier site decommission flow (#259).
 *
 * Two-step confirmation modal anchored on the site detail page.
 *
 * Stage 1 ("preflight"):
 *   - Fetch /decommission-preview on open.
 *   - Render any BLOCKERs in red with disabled "Continue" + rationale.
 *   - Render any WARNINGs in amber.
 *   - If `canDecommission`, the operator types the site name to enable
 *     the "Continue" button.
 *
 * Stage 2 ("final"):
 *   - 10-second countdown blocks the "Decommission permanently" button so
 *     the operator cannot reflexively click through.
 *   - On click, POST /decommission. On success the dialog closes and the
 *     parent is notified via `onDecommissioned`.
 *
 * Defense-in-depth: the wrapping <RoleGate role={Role.Admin}> mirrors the
 * BFF gate (#216). The actual destructive POST is gated again at both
 * the BFF and the deployment-service mirror.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, X, ShieldAlert, ShieldCheck } from 'lucide-react';
// `@jeket-cloud/types` is CJS; runtime values must be destructured from the
// namespace import (same workaround as AuthContext.tsx / RoleGate.tsx).
import { Role } from '@jeket-cloud/types/auth';
import { clusterApi, sitesApi, type DecommissionPreview } from '@/api/client';
import { RoleGate } from '@/components/auth/RoleGate';

const FINAL_COUNTDOWN_SECONDS = 10;

interface Props {
  siteId: string;
  siteName: string;
  onClose: () => void;
  onDecommissioned: () => void;
}

type Stage = 'preflight' | 'final';

function PreflightBody({ preview, isLoading, error }: {
  preview: DecommissionPreview | undefined;
  isLoading: boolean;
  error: unknown;
}) {
  if (isLoading) {
    return <div className="text-sm text-silver-muted">Running pre-flight checks…</div>;
  }
  if (error) {
    return (
      <div className="rounded-lg border border-status-error/40 bg-status-error/10 p-3 text-sm text-status-error">
        Pre-flight check failed: {error instanceof Error ? error.message : String(error)}
      </div>
    );
  }
  if (!preview) return null;

  const { blockers, warnings } = preview;

  return (
    <div className="space-y-3">
      {blockers.length === 0 && warnings.length === 0 && (
        <div
          data-testid="preview-clean"
          className="flex items-start gap-2 rounded-lg border border-status-success/40 bg-status-success/10 p-3 text-sm text-status-success"
        >
          <ShieldCheck size={16} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">No blockers detected</div>
            <div className="text-xs text-silver-muted mt-1">
              The site has no active broadcasts, helm releases, or enrolled machines.
            </div>
          </div>
        </div>
      )}

      {blockers.length > 0 && (
        <div
          data-testid="preview-blockers"
          className="rounded-lg border border-status-error/40 bg-status-error/10 p-3 space-y-2"
        >
          <div className="flex items-center gap-2 text-sm font-medium text-status-error">
            <ShieldAlert size={16} />
            Blockers
          </div>
          <ul className="space-y-1.5">
            {blockers.map((b, i) => (
              <li key={i} className="text-xs">
                <div className="font-medium text-status-error">{b.reason}</div>
                <div className="text-silver-muted mt-0.5 break-all">{b.detail}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {warnings.length > 0 && (
        <div
          data-testid="preview-warnings"
          className="rounded-lg border border-amber-400/40 bg-amber-400/10 p-3 space-y-2"
        >
          <div className="flex items-center gap-2 text-sm font-medium text-amber-300">
            <AlertTriangle size={16} />
            Warnings
          </div>
          <ul className="space-y-1.5">
            {warnings.map((w, i) => (
              <li key={i} className="text-xs">
                <div className="font-medium text-amber-200">{w.reason}</div>
                <div className="text-silver-muted mt-0.5 break-all">{w.detail}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function PreflightStage({
  siteId,
  siteName,
  onClose,
  onContinue,
}: {
  siteId: string;
  siteName: string;
  onClose: () => void;
  onContinue: () => void;
}) {
  const [confirmName, setConfirmName] = useState('');

  const { data: preview, isLoading, error } = useQuery({
    queryKey: ['decommission-preview', siteId],
    queryFn: () => clusterApi.getDecommissionPreview(siteId),
    refetchOnWindowFocus: false,
  });

  const canDecommission = preview?.canDecommission === true;
  const nameMatches = confirmName === siteName;
  const continueEnabled = canDecommission && nameMatches;

  return (
    <>
      <div className="px-6 py-4 space-y-4">
        <PreflightBody preview={preview} isLoading={isLoading} error={error} />

        {!isLoading && preview && !canDecommission && (
          <div className="text-xs text-silver-muted">
            Resolve the blockers above before this site can be decommissioned.
          </div>
        )}

        {canDecommission && (
          <div className="space-y-2">
            <label className="block text-xs text-silver-muted">
              Type the site name <span className="text-gold">{siteName}</span> to continue
            </label>
            <input
              data-testid="decommission-name-confirm"
              type="text"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder:text-silver-muted/30 focus:border-gold focus:outline-none"
              autoFocus
            />
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 px-6 py-4 border-t border-surface-elevated">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="decommission-continue"
          onClick={onContinue}
          disabled={!continueEnabled}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-status-error text-white hover:bg-status-error/80 disabled:bg-surface-elevated disabled:text-silver-muted disabled:cursor-not-allowed transition-colors"
        >
          Continue
        </button>
      </div>
    </>
  );
}

function FinalStage({
  siteId,
  siteName,
  onBack,
  onClose,
  onDecommissioned,
}: {
  siteId: string;
  siteName: string;
  onBack: () => void;
  onClose: () => void;
  onDecommissioned: () => void;
}) {
  const [secondsLeft, setSecondsLeft] = useState(FINAL_COUNTDOWN_SECONDS);
  const [toast, setToast] = useState<{ kind: 'error'; message: string } | null>(null);

  // Single setInterval instead of chained setTimeouts so fake-timer tests
  // can fast-forward the whole countdown deterministically. Stops itself
  // (via the cleared interval) once the counter hits 0.
  useEffect(() => {
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(id);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const decommissionMutation = useMutation({
    mutationFn: () => sitesApi.decommission(siteId),
    onSuccess: () => {
      onDecommissioned();
      onClose();
    },
    onError: (err) => {
      setToast({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
    },
  });

  const enabled = secondsLeft <= 0 && !decommissionMutation.isPending;

  return (
    <>
      <div className="px-6 py-4 space-y-4">
        <div className="rounded-lg border border-status-error/30 bg-status-error/5 p-3 text-sm">
          <div className="font-medium text-status-error mb-1">Final confirmation</div>
          <div className="text-xs text-silver-muted">
            About to decommission <span className="text-silver-bright">{siteName}</span>.
            This transitions the site to <span className="text-silver-bright">decommissioning</span>,
            revokes its Harbor robot, and emits a deployment.audit trail.
          </div>
        </div>

        {secondsLeft > 0 && (
          <div
            data-testid="decommission-countdown"
            className="text-center text-xs text-silver-muted"
          >
            Confirmation enabled in <span className="text-amber-300">{secondsLeft}s</span>
          </div>
        )}

        {toast && (
          <div className="rounded-lg border border-status-error/30 bg-status-error/5 px-3 py-2 text-sm text-status-error">
            {toast.message}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 px-6 py-4 border-t border-surface-elevated">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors"
        >
          Back
        </button>
        <button
          type="button"
          data-testid="decommission-confirm"
          onClick={() => decommissionMutation.mutate()}
          disabled={!enabled}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-status-error text-white hover:bg-status-error/80 disabled:bg-surface-elevated disabled:text-silver-muted disabled:cursor-not-allowed transition-colors"
        >
          {decommissionMutation.isPending ? 'Decommissioning…' : 'Decommission permanently'}
        </button>
      </div>
    </>
  );
}

export function SiteDecommissionDialog({ siteId, siteName, onClose, onDecommissioned }: Props) {
  const [stage, setStage] = useState<Stage>('preflight');

  const title = useMemo(
    () => (stage === 'preflight' ? 'Decommission site — pre-flight' : 'Decommission site — confirm'),
    [stage],
  );

  return (
    <RoleGate role={Role.Admin}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Decommission site"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
        onClick={onClose}
      >
        <div
          className="bg-surface border border-surface-elevated rounded-xl w-full max-w-lg mx-4 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="site-decommission-dialog"
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
            <h3 className="text-silver-bright font-semibold">{title}</h3>
            <button
              type="button"
              data-testid="site-decommission-close"
              onClick={onClose}
              className="text-silver-muted hover:text-silver-bright transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          {stage === 'preflight' ? (
            <PreflightStage
              siteId={siteId}
              siteName={siteName}
              onClose={onClose}
              onContinue={() => setStage('final')}
            />
          ) : (
            <FinalStage
              siteId={siteId}
              siteName={siteName}
              onBack={() => setStage('preflight')}
              onClose={onClose}
              onDecommissioned={onDecommissioned}
            />
          )}
        </div>
      </div>
    </RoleGate>
  );
}

export default SiteDecommissionDialog;
